import type { ApolloQueryResult, OperationVariables } from '@apollo/client/core';

/** The two members of a `useLazyQuery` / `use…LazyQuery` return this needs. */
export interface LazyQueryHandle<TResult, TVariables extends OperationVariables> {
  load: (document?: null, variables?: TVariables | null) => false | Promise<TResult>;
  refetch: (variables?: TVariables) => Promise<ApolloQueryResult<TResult>> | undefined;
}

/**
 * Run a lazy query now and resolve with its data, on every call.
 *
 * `load()` only starts a lazy query: once it has run, a later `load()` returns
 * `false` and sends nothing (with the same variables, not even a cache read).
 * An export button, a Refresh or a repeated Search built on `load()` alone works
 * once and then does nothing. This falls back to `refetch()` after the first
 * run, so every click reaches the server. Failures reject either way.
 */
export async function loadOrRefetch<TResult, TVariables extends OperationVariables>(
  query: LazyQueryHandle<TResult, TVariables>,
  variables?: TVariables,
): Promise<TResult | undefined> {
  const started = query.load(null, variables);
  if (started !== false) return started;
  const result = await query.refetch(variables);
  return result?.data;
}
